import { useEffect, useRef } from "react";
import { alsAnzeige, istFehler } from "../hilfen/formelrechner";
import { FORMATNAMEN, formatiere } from "../hilfen/zahlformat";
import { spaltenname, zellschluessel } from "../hilfen/zellbezug";

/*
  Das Blatt.

  ---------------------------------------------------------------
  Warum ein echtes <table> und keine Kästen
  ---------------------------------------------------------------

  Ein Raster aus `div` wäre bequemer zu gestalten. Ein `<table>` bringt aber
  drei Dinge mit, die man sonst nachbauen müsste:

    Bildschirmleser sagen „Spalte B, Zeile 4" von selbst.
    `<thead>` wiederholt sich beim Drucken über Seiten hinweg.
    Die Spaltenbreite richtet sich selbst nach dem Inhalt.

  ---------------------------------------------------------------
  Genau EIN anspringbares Element
  ---------------------------------------------------------------

  Nicht jede Zelle ist mit der Tabulatortaste erreichbar — bei zwanzig
  Zeilen und sechs Spalten wären das hundertzwanzig Stationen, bis man aus
  der Tabelle heraus wäre. Stattdessen ist das Blatt als Ganzes anspringbar,
  die gewählte Zelle steht im Zustand, und `aria-activedescendant` sagt dem
  Bildschirmleser, wo man ist.

  ---------------------------------------------------------------
  Ein Tipp wählt, der zweite öffnet
  ---------------------------------------------------------------

  Kein Doppeltipp: Den trifft mit achtzig niemand zuverlässig, und auf einem
  Tablet ist er von einem Wischen kaum zu unterscheiden. Der erste Tipp
  wählt die Zelle — ihr Inhalt erscheint dann oben in der Eingabezeile —,
  der zweite auf dieselbe Zelle öffnet sie.
*/

const BREITEN = { schmal: "6rem", normal: "9rem", breit: "14rem" };

export default function Zellraster({
  gitter,
  werte,
  gewaehlt,
  aufWahl,
  bearbeitung,
  aufOeffnen,
  aufAendern,
  aufFertig,
  aufAbbruch,
  fremde = [],
  nurLesen = false,
  aufKopf,
  aufZeilenkopf,
}) {
  const flaecheRef = useRef(null);
  const feldRef = useRef(null);

  /*
    Wem die Tastatur gerade gehört.

    Beim Öffnen dem Feld in der Zelle — und beim Schließen WIEDER DEM BLATT.
    Der Rückweg ist keine Feinheit: Ohne ihn landet der Fokus nach jedem
    Enter beim Seitenkörper, und die Tastatur ist tot. Man tippt, und nichts
    passiert; nur ein Mausklick bringt einen zurück. Beim ersten Versuch war
    genau das der Fall, und es sah aus wie ein Fehler im Schreiben.
  */
  const warOffen = useRef(false);
  useEffect(() => {
    if (bearbeitung) {
      warOffen.current = true;
      if (feldRef.current) {
        feldRef.current.focus();
        if (bearbeitung.alleMarkieren) feldRef.current.select();
        else {
          /* Schreibmarke ans Ende — das getippte Zeichen steht schon da. */
          const ende = feldRef.current.value.length;
          feldRef.current.setSelectionRange(ende, ende);
        }
      }
      return;
    }
    if (warOffen.current) {
      warOffen.current = false;
      if (flaecheRef.current) flaecheRef.current.focus();
    }
  }, [bearbeitung]);

  const { spalten, zeilen } = gitter;

  function bewege(dSpalte, dZeile) {
    const spalte = Math.max(0, Math.min(gewaehlt.spalte + dSpalte, spalten.length - 1));
    const zeile = Math.max(0, Math.min(gewaehlt.zeile + dZeile, zeilen.length - 1));
    aufWahl({ spalte, zeile });
  }

  function beiTaste(e) {
    if (bearbeitung) return;

    switch (e.key) {
      case "ArrowRight":
        e.preventDefault();
        bewege(1, 0);
        return;
      case "ArrowLeft":
        e.preventDefault();
        bewege(-1, 0);
        return;
      case "ArrowDown":
        e.preventDefault();
        bewege(0, 1);
        return;
      case "ArrowUp":
        e.preventDefault();
        bewege(0, -1);
        return;
      case "Tab":
        e.preventDefault();
        bewege(e.shiftKey ? -1 : 1, 0);
        return;
      case "Home":
        e.preventDefault();
        aufWahl({ spalte: 0, zeile: gewaehlt.zeile });
        return;
      case "End":
        e.preventDefault();
        aufWahl({ spalte: spalten.length - 1, zeile: gewaehlt.zeile });
        return;
      default:
        break;
    }

    if (nurLesen) return;

    if (e.key === "Enter" || e.key === "F2") {
      e.preventDefault();
      aufOeffnen();
      return;
    }

    if (e.key === "Delete" || e.key === "Backspace") {
      e.preventDefault();
      aufFertig("", "bleiben");
      return;
    }

    /*
      Ein druckbares Zeichen öffnet die Zelle und ersetzt ihren Inhalt — wie
      in jeder Tabellenkalkulation. Steuerzeichen und Tastenkürzel bleiben
      außen vor; sonst öffnete Strg+C eine Zelle mit dem Buchstaben c.
    */
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      aufOeffnen(e.key);
    }
  }

  function beiZelle(spalte, zeile) {
    if (gewaehlt.spalte === spalte && gewaehlt.zeile === zeile) {
      if (!nurLesen) aufOeffnen();
      return;
    }
    aufWahl({ spalte, zeile });
    if (flaecheRef.current) flaecheRef.current.focus();
  }

  return (
    <div
      className="zellraster"
      ref={flaecheRef}
      tabIndex={nurLesen ? -1 : 0}
      role="grid"
      aria-label="Tabellenblatt"
      aria-rowcount={zeilen.length + 1}
      aria-colcount={spalten.length + 1}
      aria-activedescendant={`zelle-${gewaehlt.spalte}-${gewaehlt.zeile}`}
      onKeyDown={beiTaste}
    >
      <table className="zellraster__blatt">
        <thead>
          <tr>
            {/* Die Ecke oben links — hält beide Köpfe zusammen. */}
            <th className="zellraster__ecke" scope="col">
              <span className="nur-fuer-vorleser">Zeile</span>
            </th>

            {spalten.map((spalte, nr) => (
              <th
                key={spalte.id}
                scope="col"
                className={`zellraster__kopf ${
                  nr === gewaehlt.spalte ? "zellraster__kopf--aktiv" : ""
                }`}
                style={{ minWidth: BREITEN[spalte.breite] || BREITEN.normal }}
              >
                {/*
                  Der ganze Kopf ist der Knopf — sonst müsste man einen
                  einzelnen Buchstaben treffen. Darunter steht das Format
                  im Klartext: Ob eine Spalte Euro zeigt, ist beim Lesen
                  einer Haushaltsliste eine der ersten Fragen, und die
                  Antwort gehört an die Spalte, nicht in ein Menü.
                */}
                <button
                  type="button"
                  className="zellraster__kopfknopf"
                  onClick={(e) => aufKopf && aufKopf(nr, e.currentTarget)}
                  disabled={!aufKopf}
                  title={`Spalte ${spaltenname(nr)} — einfügen, löschen, Format, Breite`}
                >
                  <span className="zellraster__kopfname">{spaltenname(nr)}</span>
                  <span className="zellraster__kopfformat">
                    {FORMATNAMEN[spalte.format] || ""}
                  </span>
                </button>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {zeilen.map((zeile, zNr) => (
            <tr key={zeile.id}>
              <th
                scope="row"
                className={`zellraster__zeilenkopf ${
                  zNr === gewaehlt.zeile ? "zellraster__kopf--aktiv" : ""
                }`}
              >
                <button
                  type="button"
                  className="zellraster__kopfknopf"
                  onClick={(e) => aufZeilenkopf && aufZeilenkopf(zNr, e.currentTarget)}
                  disabled={!aufZeilenkopf}
                  title={`Zeile ${zNr + 1} — einfügen, löschen, verschieben`}
                >
                  {zNr + 1}
                </button>
              </th>

              {spalten.map((spalte, sNr) => {
                const schluessel = zellschluessel(spalte.id, zeile.id);
                const istGewaehlt = gewaehlt.spalte === sNr && gewaehlt.zeile === zNr;
                const offen = istGewaehlt && bearbeitung;
                const wert = werte.get(schluessel);
                const anzeige = alsAnzeige(wert);
                const daneben = istFehler(wert);
                const fremd = fremde.find((f) => f.spalte === sNr && f.zeile === zNr);

                return (
                  <td
                    key={spalte.id}
                    id={`zelle-${sNr}-${zNr}`}
                    role="gridcell"
                    aria-selected={istGewaehlt}
                    className={[
                      "zellraster__zelle",
                      istGewaehlt ? "zellraster__zelle--gewaehlt" : "",
                      anzeige.zahl !== null ? "zellraster__zelle--zahl" : "",
                      daneben ? "zellraster__zelle--fehler" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => beiZelle(sNr, zNr)}
                  >
                    {offen ? (
                      <input
                        ref={feldRef}
                        className="zellraster__feld"
                        type="text"
                        value={bearbeitung.text}
                        onChange={(e) => aufAendern(e.target.value)}
                        onKeyDown={(e) => beiFeldTaste(e, aufFertig, aufAbbruch)}
                        onBlur={() => aufFertig(bearbeitung.text, "bleiben")}
                        aria-label={`${spaltenname(sNr)}${zNr + 1}`}
                      />
                    ) : (
                      <>
                        {anzeige.zahl !== null
                          ? formatiere(anzeige.zahl, spalte.format)
                          : anzeige.text}
                        {fremd && (
                          <span
                            className="zellraster__fremd"
                            style={{ background: fremd.farbe }}
                            title={`${fremd.name} steht hier`}
                          />
                        )}
                      </>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/*
  Die Tasten im geöffneten Feld.

  Enter geht nach unten, Umschalt+Enter nach oben, Tabulator nach rechts —
  so wandert man beim Eintippen einer Spalte von selbst weiter, ohne die
  Hand von der Tastatur zu nehmen.
*/
function beiFeldTaste(e, aufFertig, aufAbbruch) {
  /*
    IMMER anhalten — auch bei Enter, Tabulator und Escape.

    Sonst steigt der Tastendruck bis zum Blatt auf, und das öffnet die Zelle
    sofort wieder. React verarbeitet den Zustand bei Tastendrücken nämlich
    noch WÄHREND das Ereignis aufsteigt: Das Blatt sieht dann bereits
    „keine Zelle offen" und tut, was es bei Enter eben tut — es öffnet eine.
    Beim ersten Versuch stand danach ein leeres Feld da, und der getippte
    Text war fort.
  */
  e.stopPropagation();

  if (e.key === "Enter") {
    e.preventDefault();
    aufFertig(e.target.value, e.shiftKey ? "hoch" : "runter");
    return;
  }
  if (e.key === "Tab") {
    e.preventDefault();
    aufFertig(e.target.value, e.shiftKey ? "links" : "rechts");
    return;
  }
  if (e.key === "Escape") {
    e.preventDefault();
    aufAbbruch();
  }
}
